// Global Types
import type {
  Dispatch,
  Store,
} from 'redux';

// Helpers
import {
  applyMiddleware,
  combineReducers,
  compose,
  createStore,
} from 'redux';
import logger from 'redux-logger';
import {
  withExtraArgument,
} from 'redux-thunk';
import promise from 'redux-promise-middleware';
/* eslint-disable-next-line import/no-named-as-default */
import themeMiddlewares from '../theme/store/middlewares/middlewares';

import mainReducer, {
  initialState as mainReducerInitialState,
} from './modules/main';
import authReducer, {
  initialState as authReducerInitialState,
} from './modules/auth';
import settingsReducer, {
  initialState as settingsReducerInitialState,
} from './modules/settings';
import themeReducer, {
  initialState as themeReducerInitialState,
} from './modules/theme';

// Types
import type {
  $ActionTypes as $MainActionTypes,
  $Reducer as $MainReducer,
  $State as $MainState,
} from './modules/main';
import type {
  $ActionTypes as $AuthActionTypes,
  $Reducer as $AuthReducer,
  $State as $AuthState,
} from './modules/auth';
import type {
  $Reducer as $SettingsReducer,
  $State as $SettingsState,
} from './modules/settings';
import type {
  $Reducer as $ThemeReducer,
  $State as $ThemeState,
} from './modules/theme';

import type {
  $Services,
} from '../services';

type $ActionTypes = $AuthActionTypes | $MainActionTypes;

export type $ActionEnvelope<T> = {
  payload?: T;
  type: $ActionTypes;
};

export type $ActionResult<P> = Promise<{
  value: P;
}>;

export type $State = {
  auth: $AuthState;
  main: $MainState;
  settings: $SettingsState;
  theme: $ThemeState;
};

export type $ReducerState<R extends keyof $State> = $State[R];

type $ReducerAction<R extends keyof $State> = (state: $State[R], payload) => $State[R];

export type $ReducerActions<R extends keyof $State> = Record<string, $ReducerAction<R>>;

export type $Dispatch = Dispatch<$ActionEnvelope<unknown>>;

type $DispatchHandler<T> = (
  dispatch: $Dispatch,
  getState: () => $State,
  services: $Services,
) => $ActionEnvelope<T>;

export type $Action<R, P> = (
  params: P,
) => $DispatchHandler<(() => Promise<R>) | Promise<R>>;

type $Reducers = {
  auth: $AuthReducer;
  main: $MainReducer;
  settings: $SettingsReducer;
  theme: $ThemeReducer;
};

export const initialState: $State = {
  auth: authReducerInitialState,
  main: mainReducerInitialState,
  settings: settingsReducerInitialState,
  theme: themeReducerInitialState,
};

const reducers = combineReducers<$Reducers>({
  auth: authReducer,
  main: mainReducer,
  settings: settingsReducer,
  theme: themeReducer,
});

export type $Store = Store<$State>;

const setupDevToolsExtension = () => ((typeof window !== 'undefined' && window.devToolsExtension)
  ? window.devToolsExtension()
  : ((f) => f)
);

const configureStore = (services: $Services, providedState?: $State): $Store => {
  const middlewares = [withExtraArgument(services), promise, ...themeMiddlewares];

  if (process.env.NODE_ENV === 'development') {
    middlewares.push(logger);
  }

  const state = providedState || initialState;

  return createStore(
    reducers,
    state,
    // @ts-expect-error redux
    compose(
      applyMiddleware(...middlewares),
      setupDevToolsExtension(),
    ),
  );
};

export default configureStore;
